const API_BASE =
  process.env.REACT_APP_AZURE_API_BASE_URL ||
  process.env.REACT_APP_API_BASE_URL_PROD ||
  '/api/v1';

export const LOCALOPS_LAUNCH_SESSION_KEY = 'jg:localopsLaunchSessionId';

export const LAUNCH_VISIT_EVENTS = Object.freeze({
  LANDING_VIEWED: 'localops_region_landing_viewed',
  ENGAGED_10S: 'localops_region_engaged_10s',
  ENGAGED_60S: 'localops_region_engaged_60s',
  CTA_CLICKED: 'localops_region_cta_clicked',
});

export const normalizeLocalOpsLaunchTag = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return '';
  return raw
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
};

const getSearchValue = (searchParams, key) => {
  if (!searchParams) return '';
  if (typeof searchParams.get === 'function') return searchParams.get(key) || '';
  return searchParams[key] || '';
};

export const getLocalOpsLaunchSourceTag = (searchParams) =>
  normalizeLocalOpsLaunchTag(
    getSearchValue(searchParams, 'src') ||
      getSearchValue(searchParams, 'source') ||
      getSearchValue(searchParams, 'utm_source') ||
      getSearchValue(searchParams, 'utm_campaign') ||
      '',
  );

const createAnonymousSessionId = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `localops-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const getLocalOpsLaunchSessionId = () => {
  if (typeof window === 'undefined') return createAnonymousSessionId();
  try {
    const existing = window.sessionStorage?.getItem(LOCALOPS_LAUNCH_SESSION_KEY);
    if (existing) return existing;
    const next = createAnonymousSessionId();
    window.sessionStorage?.setItem(LOCALOPS_LAUNCH_SESSION_KEY, next);
    return next;
  } catch (err) {
    return createAnonymousSessionId();
  }
};

const buildEndpoint = (slug) => {
  const base = String(API_BASE || '/api/v1').replace(/\/+$/, '');
  return `${base}/offline-regions/${encodeURIComponent(slug)}/launch-events`;
};

const isSameOriginEndpoint = (endpoint) => {
  if (typeof window === 'undefined' || !window.location) return false;
  try {
    return new URL(endpoint, window.location.href).origin === window.location.origin;
  } catch (err) {
    return false;
  }
};

export const buildLocalOpsLaunchEventPayload = ({
  eventName,
  sourceTag,
  routeKind,
  intent,
  ctaId,
} = {}) => ({
  eventName,
  anonymousSessionId: getLocalOpsLaunchSessionId(),
  src: normalizeLocalOpsLaunchTag(sourceTag) || undefined,
  routeKind: normalizeLocalOpsLaunchTag(routeKind) || 'region_landing',
  intent: normalizeLocalOpsLaunchTag(intent) || undefined,
  ctaId: normalizeLocalOpsLaunchTag(ctaId) || undefined,
});

export const trackLocalOpsLaunchEvent = ({
  slug,
  eventName,
  sourceTag,
  routeKind,
  intent,
  ctaId,
} = {}) => {
  if (!slug || !eventName) return Promise.resolve(false);
  const payload = buildLocalOpsLaunchEventPayload({
    eventName,
    sourceTag,
    routeKind,
    intent,
    ctaId,
  });
  const endpoint = buildEndpoint(slug);
  const body = JSON.stringify(payload);

  try {
    if (
      isSameOriginEndpoint(endpoint) &&
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function'
    ) {
      const sent = navigator.sendBeacon(
        endpoint,
        new Blob([body], { type: 'application/json' }),
      );
      if (sent) return Promise.resolve(true);
    }
  } catch (err) {
    // best effort only
  }

  try {
    if (typeof fetch !== 'function') return Promise.resolve(false);
    return fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body,
      keepalive: true,
      credentials: 'omit',
    })
      .then(() => true)
      .catch(() => false);
  } catch (err) {
    return Promise.resolve(false);
  }
};
